import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Home } from 'lucide-react';
import { patientService, doctorService, appointmentService } from '../../lib/services';
import { appointmentSchema, type AppointmentFormData } from '../../lib/validation';
import type { Patient, Doctor } from '../../lib/supabase';
import toast from 'react-hot-toast';

export default function CreateAppointment() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [filteredDoctors, setFilteredDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(true);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<AppointmentFormData>({
    resolver: zodResolver(appointmentSchema),
    defaultValues: {
      status: 'scheduled',
      appointment_charge: 0,
      payment_status: 'unpaid',
      payment_type: 'cash',
      prescription_status: 'none',
    },
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [patientsData, doctorsData] = await Promise.all([
        patientService.getAll(),
        doctorService.getAll(),
      ]);
      setPatients(patientsData);
      setDoctors(doctorsData);
      setFilteredDoctors(doctorsData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const filteredPatients = patients.filter(patient => {
    const search = patientSearch.toLowerCase();
    return (
      patient.name.toLowerCase().includes(search) ||
      patient.unique_id?.toLowerCase().includes(search) ||
      patient.phone?.toLowerCase().includes(search)
    );
  }).slice(0, 10);

  const departments = Array.from(new Set(doctors.map(d => d.specialization).filter(Boolean)));

  const filterDoctorsByDepartment = (dept: string) => {
    if (!dept) {
      setFilteredDoctors(doctors);
    } else {
      setFilteredDoctors(doctors.filter(d => d.specialization === dept));
    }
  };

  const handlePatientSelect = (patient: Patient) => {
    setSelectedPatient(patient);
    setPatientSearch(patient.name);
    setValue('patient_id', patient.id);
    setValue('contact_number', patient.phone || '');
    setShowPatientDropdown(false);
  };

  const handleDepartmentChange = (dept: string) => {
    setSelectedDepartment(dept);
    setValue('department', dept);
    filterDoctorsByDepartment(dept);
    setValue('doctor_id', '');
  };

  const onSubmit: SubmitHandler<AppointmentFormData> = async (data) => {
    try {
      const formData = {
        ...data,
        end_time: data.start_time,
      };
      
      await appointmentService.create(formData);
      toast.success('Appointment created successfully');
      navigate('/appointments');
    } catch (error: any) {
      console.error('Error creating appointment:', error);
      toast.error(error.message || 'Failed to create appointment');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <Home className="w-4 h-4" />
          <span>Create Appointments</span>
        </div>
      </div>

      {/* Form Container */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Find Patient */}
            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-right pt-2 text-sm font-medium text-gray-700">
                Find Patient: <span className="text-red-500">*</span>
              </label>
              <div className="col-span-2 relative">
                <input
                  type="text"
                  value={patientSearch}
                  onChange={(e) => {
                    setPatientSearch(e.target.value);
                    setShowPatientDropdown(true);
                  }}
                  onFocus={() => setShowPatientDropdown(true)}
                  placeholder="ID, Name, Phone"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
                {showPatientDropdown && patientSearch && filteredPatients.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {filteredPatients.map((patient) => (
                      <button
                        key={patient.id}
                        type="button"
                        onClick={() => handlePatientSelect(patient)}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-100"
                      >
                        <div className="font-medium">{patient.name}</div>
                        <div className="text-sm text-gray-500">
                          ID: {patient.unique_id} | Phone: {patient.phone}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                <input type="hidden" {...register('patient_id')} />
                {errors.patient_id && <p className="mt-1 text-sm text-red-600">{errors.patient_id.message}</p>}
              </div>
            </div>

            {/* Contact Number */}
            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-right pt-2 text-sm font-medium text-gray-700">
                Contact Number:
              </label>
              <div className="col-span-2">
                <input
                  {...register('contact_number')}
                  type="text"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Doctor Department */}
            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-right pt-2 text-sm font-medium text-gray-700">
                Doctor Department: <span className="text-red-500">*</span>
              </label>
              <div className="col-span-2">
                <select
                  value={selectedDepartment}
                  onChange={(e) => handleDepartmentChange(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">--Select--</option>
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Doctor */}
            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-right pt-2 text-sm font-medium text-gray-700">
                Doctor: <span className="text-red-500">*</span>
              </label>
              <div className="col-span-2">
                <select
                  {...register('doctor_id')}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">--Select--</option>
                  {filteredDoctors.map((doctor) => (
                    <option key={doctor.id} value={doctor.id}>
                      {doctor.name} - {doctor.specialization}
                    </option>
                  ))}
                </select>
                {errors.doctor_id && <p className="mt-1 text-sm text-red-600">{errors.doctor_id.message}</p>}
              </div>
            </div>

            {/* Appointment Charge */}
            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-right pt-2 text-sm font-medium text-gray-700">
                Appointment Charge:
              </label>
              <div className="col-span-2">
                <input
                  {...register('appointment_charge', { valueAsNumber: true })}
                  type="number"
                  step="0.01"
                  min="0"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
                {errors.appointment_charge && <p className="mt-1 text-sm text-red-600">{errors.appointment_charge.message}</p>}
              </div>
            </div>

            {/* Payment Type */}
            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-right pt-2 text-sm font-medium text-gray-700">
                Payment Type: <span className="text-red-500">*</span>
              </label>
              <div className="col-span-2">
                <select
                  {...register('payment_type')}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="insurance">Insurance</option>
                </select>
              </div>
            </div>

            {/* Date */}
            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-right pt-2 text-sm font-medium text-gray-700">
                Date: <span className="text-red-500">*</span>
              </label>
              <div className="col-span-2">
                <input
                  {...register('appointment_date')}
                  type="date"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
                {errors.appointment_date && <p className="mt-1 text-sm text-red-600">{errors.appointment_date.message}</p>}
              </div>
            </div>

            {/* Time */}
            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-right pt-2 text-sm font-medium text-gray-700">
                Time: <span className="text-red-500">*</span>
              </label>
              <div className="col-span-2">
                <input
                  {...register('start_time')}
                  type="time"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
                {errors.start_time && <p className="mt-1 text-sm text-red-600">{errors.start_time.message}</p>}
              </div>
            </div>

            {/* Hidden end_time field */}
            <input type="hidden" {...register('end_time')} value={watch('start_time')} />

            {/* Payment Status */}
            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-right pt-2 text-sm font-medium text-gray-700">
                Payment Status: <span className="text-red-500">*</span>
              </label>
              <div className="col-span-2">
                <div className="flex space-x-6">
                  <label className="inline-flex items-center">
                    <input
                      {...register('payment_status')}
                      type="radio"
                      value="paid"
                      className="form-radio text-primary-600"
                    />
                    <span className="ml-2">Paid</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      {...register('payment_status')}
                      type="radio"
                      value="unpaid"
                      className="form-radio text-primary-600"
                    />
                    <span className="ml-2">Unpaid</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="grid grid-cols-3 gap-4 items-start">
              <label className="text-right pt-2 text-sm font-medium text-gray-700">
                Description:
              </label>
              <div className="col-span-2">
                <textarea
                  {...register('notes')}
                  rows={4}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Hidden fields with defaults */}
            <input type="hidden" {...register('status')} value="scheduled" />
            <input type="hidden" {...register('prescription_status')} value="none" />

            {/* Submit Button */}
            <div className="grid grid-cols-3 gap-4">
              <div></div>
              <div className="col-span-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-8 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
